import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { backupsAPI } from '@hpanel/web-common/api/client'
import type { BackupResponse, Domain } from '@hpanel/web-common/api/types'
import { DownloadBackupButton } from './DownloadBackupButton'

interface Props {
  backups: Array<BackupResponse & { tiers?: string | null }>
  domains: Domain[]
  onDeleted: () => void
  onError: (msg: string) => void
}

function formatBytes(n: number | null): string {
  if (n === null) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return iso.replace('T', ' ').slice(0, 16)
}

function statusBadge(status: string): React.ReactNode {
  const cls: Record<string, string> = {
    success:
      'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
    running: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
    pending:
      'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  }
  const label: Record<string, string> = {
    success: '✓ Ready',
    failed: '✗ Failed',
    // The spinner ▸ pre-icon shows the user the row is *live* and
    // worth keeping the page open. Plan 20 P1.
    running: '⟳ Backing up…',
    pending: '⏳ Queued',
  }
  return (
    <span
      data-testid={`backups-status-${status}`}
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
    >
      {label[status] ?? status}
    </span>
  )
}

function elapsedSince(iso: string | null): string | null {
  if (!iso) return null
  const start = new Date(iso).getTime()
  if (Number.isNaN(start)) return null
  const seconds = Math.max(0, Math.floor((Date.now() - start) / 1000))
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s}s`
}

/**
 * Table of backup records with download and delete actions.
 *
 * Key data-testid attributes (asserted by QA):
 *   backups-list-table       — the <table> element
 *   backups-row-{id}         — each <tr>
 *   backups-download-{id}    — the Download button (only shown for status=success)
 */
export const BackupList: React.FC<Props> = ({
  backups,
  domains,
  onDeleted,
  onError,
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const domainNameFor = (containerId: number | null): string => {
    const d = domains.find((dom) => dom.container_id === containerId)
    return d ? d.name : `container #${containerId}`
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this backup record? The on-disk archive is retained by the operator.')) return
    setDeletingId(id)
    try {
      await backupsAPI.delete(id)
      onDeleted()
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  if (backups.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        No backups yet. Use the <strong>Create backup</strong> button to make your first one.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <table
        data-testid="backups-list-table"
        className="w-full text-sm"
      >
        <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3">Domain / Container</th>
            <th className="px-4 py-3">Kind</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Size</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {backups.map((b) => {
            const isInFlight =
              b.status === 'pending' || b.status === 'running'
            const isAccount = b.kind === 'account_full'
            return (
              <tr
                key={b.id}
                data-testid={`backups-row-${b.id}`}
                className="border-t border-gray-200 dark:border-gray-700"
              >
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  {isAccount
                    ? 'Account (all domains)'
                    : domainNameFor(b.container_id)}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                  {b.kind}
                  {(b.tiers ?? '')
                    .split(',')
                    .filter(Boolean)
                    .map((t) => (
                      <span
                        key={t}
                        data-testid={`backups-tier-${b.id}-${t}`}
                        className="ml-1 inline-block px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200"
                      >
                        {t}
                      </span>
                    ))}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                  {formatDate(b.created_at)}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                  {formatBytes(b.size_bytes)}
                </td>
                <td className="px-4 py-3">
                  {statusBadge(b.status)}
                  {isInFlight && (
                    <span
                      data-testid={`backups-elapsed-${b.id}`}
                      className="ml-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                      {elapsedSince(b.started_at ?? b.created_at) ?? ''}
                    </span>
                  )}
                  {b.status === 'failed' && b.error && (
                    <div
                      data-testid={`backups-error-${b.id}`}
                      className="mt-1 text-xs text-red-700 dark:text-red-300 max-w-xs"
                      title={b.error}
                    >
                      {b.error.length > 80 ? `${b.error.slice(0, 80)}…` : b.error}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  {b.status === 'success' && (
                    <DownloadBackupButton backupId={b.id} onError={onError} />
                  )}
                  {b.status === 'success' && isAccount && (
                    <Link
                      to={`/client/backups/${b.id}/restore`}
                      data-testid={`backups-restore-${b.id}`}
                      className="px-2 py-1 border border-primary-600 text-primary-700 dark:text-primary-300 text-xs rounded"
                    >
                      Restore
                    </Link>
                  )}
                  {isInFlight && (
                    <span
                      className="inline-block px-2 py-1 text-xs text-gray-500 dark:text-gray-400 italic"
                      data-testid={`backups-in-progress-${b.id}`}
                    >
                      Download available when ready
                    </span>
                  )}
                  <button
                    disabled={deletingId === b.id || isInFlight}
                    onClick={() => void handleDelete(b.id)}
                    title={
                      isInFlight
                        ? 'Wait for the backup to finish before deleting'
                        : 'Delete this backup record'
                    }
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
